import flattenDeep from 'lodash/flattenDeep';
import React, { useEffect, useRef } from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { Trans } from 'react-i18next';

import { defaultSuccess } from '@legacy-libs/utils';
import { uploadTemplate } from '@legacy-services/api/registrations';

import { getTrackedJob } from '@shared/tracked-jobs/endpoints/getTrackedJob';
import { Portlet } from '@shared/ui/components/Portlet';

import Confirm from '../../../../../base/Confirm';

type TrackedJobData = App.Domain.TrackedJobs.Data.TrackedJobData;

interface Registration {
  id: string;
  downloadCrewMembersImportTemplateUrl?: string;
}

interface CrewMemberDropzoneProps {
  registration: Registration;
}

const CrewMemberDropzone: React.FC<CrewMemberDropzoneProps> = ({ registration }) => {
  const [errors, setErrors] = React.useState<string[]>([]);
  const [trackedJob, setTrackedJob] = React.useState<TrackedJobData | null>(null);
  const [isPolling, setIsPolling] = React.useState<boolean>(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  // Poll for job status when trackedJob exists
  useEffect(() => {
    if (trackedJob && trackedJob.uuid && isPolling) {
      const pollJob = async () => {
        try {
          const updatedJob = await getTrackedJob(trackedJob.uuid);
          setTrackedJob(updatedJob);

          // Continue polling if job is still in progress
          if (updatedJob.status !== 'finished' && updatedJob.status !== 'failed') {
            pollingTimeoutRef.current = setTimeout(pollJob, 5000); // Poll every 5 seconds
          } else {
            setIsPolling(false);
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          setIsPolling(false);
        }
      };

      pollJob();
    }
  }, [isPolling]);

  function onDrop(acceptedFiles: File[], rejectedFiles: FileRejection[]) {
    Confirm('Are you sure you want to overwrite all data with the data in the file you are uploading?', {
      title: 'Upload Crew Members',
      confirmStyle: 'btn-danger',
      confirmText: 'Import',
    }).then(
      () => {
        acceptedFiles.forEach((file) => {
          let data = new FormData();
          data.append('excel', file);
          uploadTemplate(registration.id, data)
            .then((response: TrackedJobData) => {
              setTrackedJob(response);
              setIsPolling(true);
              defaultSuccess('Upload successful, your excel is now being processed...');
            })
            .catch((e) => {
              setErrors(e.response.data);
            });
        });
      },
      () => {}
    );
  }

  const renderJobStatus = () => {
    if (!trackedJob) return null;

    if (trackedJob.status === 'finished' || trackedJob.status === 'failed') {
      if (trackedJob.output) {
        const parsedOutput = JSON.parse(trackedJob.output);
        const isArray = Array.isArray(parsedOutput);

        return (
          <div className="mt-4">
            <h4>Import {trackedJob.status}</h4>
            {isArray ? (
              <ul className="p-3 bg-light border rounded">
                {flattenDeep(parsedOutput).map((item, index) => (
                  <li key={index}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
                ))}
              </ul>
            ) : (
              <pre className="p-3 bg-light border rounded">{JSON.stringify(parsedOutput, null, 2)}</pre>
            )}
          </div>
        );
      }

      return (
        <div className="mt-4">
          <h4>Import {trackedJob.status}</h4>
          <p>No output available</p>
        </div>
      );
    } else {
      return (
        <div className="mt-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <span>Importing ({trackedJob.status})...</span>
          </div>
        </div>
      );
    }
  };

  return (
    <Portlet
      title="Crew member Excel import"
      icon="import"
      actions={
        <>
          <a
            className="btn btn-primary m--margin-bottom-10"
            href={registration?.downloadCrewMembersImportTemplateUrl}
            download
          >
            <span className="la la-download" />
            <Trans>CrewMemberExcelDownload</Trans>
          </a>
        </>
      }
    >
      {registration && (
        <div>
          <div className="row justify-content-center">
            <div className="col-lg-6 col-lg-auto">
              {!isPolling && (
                <Dropzone onDrop={onDrop}>
                  {({ getRootProps }) => (
                    <div {...getRootProps()} className="m-dropzone dropzone dz-clickable">
                      <div className="m-dropzone__msg dz-message needsclick">
                        <h3 className="m-dropzone__msg-title">
                          <Trans>frontend.crew.upload.title</Trans>
                        </h3>
                        <span className="m-dropzone__msg-desc">
                          <Trans>frontend.crew.upload.description</Trans>
                        </span>
                      </div>
                    </div>
                  )}
                </Dropzone>
              )}

              {renderJobStatus()}

              {errors.length > 0 && (
                <div className="alert alert-danger mt-3">
                  <ul>
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Portlet>
  );
};

export default CrewMemberDropzone;
